import classNames from "classnames";
import { CitationTag } from "components/CitationTag";
import Popover from "components/Popover";
import MeterIndicatorTooltip from "components/Popover/MeterIndicatorTooltip/MeterIndicatorTooltip";
import MeterTag from "components/Tag/tags/MeterTag";
import RetractedTag from "components/Tag/tags/RetractedTag";
import { ResultFormat } from "components/Button/ResultFormatButton";
import useLabels from "hooks/useLabels";
import { PaperTitleProps } from "../../../types";
import { CITATION_WIDTH } from "../../../constants";

/**
 * @component PaperTitle
 * @description Renders paper title with citation, meter tag, and retracted state
 */
export const PaperTitle = ({
  paper,
  citation,
  meter,
  isLoadingTitle = false,
  isRetracted = false,
  showCitationDashes = false,
  resultFormat,
  queryText = "",
  className,
}: PaperTitleProps) => {
  const [searchLabels] = useLabels("screens.search");

  const renderCitationAndTitle = () => {
    return (
      <div className="flex flex-nowrap gap-3 justify-start items-center w-full">
        {citation !== undefined && !isRetracted && (
          <Popover
            interactive
            maxWidth={306}
            appendToParent
            disabled={resultFormat !== ResultFormat.COMPACT || !meter}
            tooltipContent={
              <MeterIndicatorTooltip meter={meter} query={queryText} />
            }
          >
            {/* Citation size is 29px to match the largest citation tag size */}
            <div
              className="flex justify-center"
              style={{ width: CITATION_WIDTH }}
            >
              <CitationTag
                paperIndex={showCitationDashes ? undefined : citation}
                variant={meter}
              />
            </div>
          </Popover>
        )}
        {isLoadingTitle ? (
          <div className="space-y-2 w-full animate-pulse">
            <div className="bg-bg-subtle h-4 w-[75%] rounded-xl" />
          </div>
        ) : (
          <div className="flex flex-1 gap-3 items-center min-w-0">
            {isRetracted && <RetractedTag />}
            <h2 className="flex-1 min-w-0 text-left base-md text-fg-base content line-clamp-2 group-hover:underline">
              {isRetracted ? searchLabels["retracted"] : ""}
              {paper.title}
            </h2>
          </div>
        )}
      </div>
    );
  };

  const renderExpandedMeterTag = () => {
    if (resultFormat !== ResultFormat.DEFAULT || !meter) return null;

    return (
      <div className="hidden sm:flex">
        <Popover
          interactive
          appendToParent
          tooltipContent={
            <MeterIndicatorTooltip meter={meter} query={queryText} />
          }
        >
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <MeterTag meter={meter} />
          </div>
        </Popover>
      </div>
    );
  };

  if (resultFormat === ResultFormat.TABLE) {
    return renderCitationAndTitle();
  }

  return (
    <div className={className}>
      <div className="flex items-center gap-3">
        {/* Citation and Title */}
        {renderCitationAndTitle()}

        {/* Expanded Meter Tag */}
        {renderExpandedMeterTag()}
      </div>
    </div>
  );
};

export default PaperTitle;
